import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { Notification as customNotification } from '../../../components/ui/custom-notification';
import { fetchProjectInformation } from '../api/projectInformation';
import { updateProjectSettings } from '../api/projectSettings';

interface ProjectSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectNumber: string;
  onSaved?: () => void;
}

export const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  open,
  onOpenChange,
  projectNumber,
  onSaved,
}) => {
  const [targetNumberOfPipesEnds, setTargetNumberOfPipesEnds] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !projectNumber.trim()) return;

    let cancelled = false;
    setLoading(true);
    fetchProjectInformation(projectNumber)
      .then((info) => {
        if (cancelled || !info) return;
        setTargetNumberOfPipesEnds(
          info.targetNumberOfPipesEnds != null ? String(info.targetNumberOfPipesEnds) : ''
        );
      })
      .catch((error: any) => {
        if (cancelled) return;
        customNotification.error(
          'Could not load settings',
          error?.message || 'Please try again.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, projectNumber]);

  const handleSave = async () => {
    if (!projectNumber.trim()) return;
    setSaving(true);
    try {
      const endsTrimmed = targetNumberOfPipesEnds.trim();
      await updateProjectSettings(projectNumber, {
        targetNumberOfPipesEnds: endsTrimmed === '' ? null : Number(endsTrimmed),
      });

      customNotification.success('Project settings saved');
      onSaved?.();
      onOpenChange(false);
    } catch (error: any) {
      customNotification.error(
        'Save failed',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md bg-white">
        <DialogHeader className="pl-0 pr-14 pb-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Settings
            </DialogTitle>
            <Button
              onClick={handleSave}
              disabled={saving || loading || !projectNumber.trim()}
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-text-grey text-[13px]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading settings...
          </div>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="target-pipe-ends" className="text-[12px] font-medium text-text-grey">
            Daily Scanning Target (Pipe Ends)
            </Label>
            <Input
              id="target-pipe-ends"
              type="number"
              min={0}
              value={targetNumberOfPipesEnds}
              onChange={(e) => setTargetNumberOfPipesEnds(e.target.value)}
              placeholder="Optional"
              className="rounded-lg border-[#E2E5EA] bg-white text-text-grey focus-visible:border-blueprint-blue focus-visible:ring-1 focus-visible:ring-blueprint-blue focus-visible:ring-offset-0"
              disabled={saving}
              autoFocus
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSettingsModal;
